import React, { useReducer, useCallback } from "react";
import clsx from "clsx";
import { useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import useStyles, { BLOCK_MIN_HEIGHT } from "./styles";

export interface TabTable {
  onLoad?: (items, pagingInfo) => void;
}

interface ITab {
  name: string;
  render?: () => React.ReactElement;
  component?: React.ReactElement;
}

interface Props {
  tabs: ITab[];
  defaultOpen?: number;
}

const getInitialState = defaultActiveTab => {
  return {
    activeTab: defaultActiveTab,
    minHeight: BLOCK_MIN_HEIGHT,
    data: {},
    loading: {},
    totalResults: {},
    alert: {}
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":
      return {
        ...state,
        data: {
          ...state.data,
          [action.collectionName]: action.collection
        },
        loading: {
          ...state.loading,
          [action.collectionName]: false
        },
        totalResults: {
          ...state.totalResults,
          [action.collectionName]: action.totalResults
        },
        alert: {
          ...state.alert,
          [action.collectionName]: action.alert
        }
      };
    case "CHANGE_TAB":
      return {
        ...state,
        activeTab: action.activeTab
      };
    case "CHANGE_HEIGHT":
      return {
        ...state,
        minHeight: action.minHeight
      };

    default:
      return state;
  }
};

const TabTables = (props: Props) => {
  const { tabs, defaultOpen = 0 } = props;
  const classes = useStyles();

  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up("md"), {
    noSsr: true
  });

  const [state, dispatch] = useReducer(
    reducer,
    getInitialState(tabs[defaultOpen].name)
  );

  const hasActivity = tabs.some(t => Boolean(state.totalResults[t.name]));

  const isLoading = tabs.some(t => Boolean(state.loading[t.name]));

  const handleCollectionLoad = useCallback(
    collectionName =>
      (items, pagingInfo = null, alert = false) => {
        dispatch({
          type: "LOAD",
          collectionName,
          collection: items,
          alert,
          totalResults: pagingInfo?.totalResults || items?.length
        });
      },
    [dispatch]
  );

  const handleTabChange = useCallback(
    (e, v) => {
      dispatch({ type: "CHANGE_TAB", activeTab: v });
    },
    [dispatch]
  );

  return (
    <Paper
      className={clsx(classes.activityTablePaper, {
        [classes.hasActivity]: hasActivity
      })}
      style={{
        minHeight: matchesUpMd ? state.minHeight : 0
      }}
    >
      <Tabs
        value={state.activeTab}
        onChange={handleTabChange}
        className={classes.tabs}
        variant="fullWidth"
      >
        {tabs.map(t => (
          <Tab
            className={clsx({ [classes.alert]: state.alert[t.name] })}
            key={t.name}
            label={`${t.name} (${state.totalResults[t.name] || 0})`}
            value={t.name}
          />
        ))}
      </Tabs>
      <Box className={classes.tabPanelWrapper}>
        {tabs.map(t => (
          <Box
            key={t.name}
            className={clsx(classes.tabPanel, {
              [classes.tabPanelVisible]: state.activeTab === t.name
            })}
          >
            {React.cloneElement(t.component, {
              onLoad: handleCollectionLoad(t.name)
            })}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default TabTables;
